import React, { useEffect } from 'react'
import useReactRouter from 'use-react-router'
import { Table, Popconfirm, Layout, PageHeader } from 'antd'
import notification from 'mrx-notification'
import { ColumnsType } from 'antd/lib/table'
import useHeightResize from 'utils/useHeightResize'
import { Breadcrumbs } from 'components'
import * as rootStyles from 'assets/layoutStyle'
import styles from './AwsSettingsList.module.scss'
import { connector, PropsFromRedux } from './container'
import { AwsSetting } from '../../types'
import CreateAwsSetting from '../CreateAwsSetting'

type HeightResizeRule = [number, number, number]
const rules: HeightResizeRule[] = [
  [0, 800, 8],
  [800, Infinity, 10]
]

const AwsSettingsList = (props: PropsFromRedux) => {
  const {
    awsSettings,
    isLoading,
    error,
    isNeedToFetchList,
    total,
    currentPage,

    changeCurrentPage,
    changePageSize,
    fetchAwsSettingsList,
    removeAwsSetting
  } = props

  const { history } = useReactRouter()

  const handleChangePageSize = (newSize: number) => {
    changePageSize(newSize)
    fetchAwsSettingsList()
  }

  useHeightResize(handleChangePageSize, rules)

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Aws settings list page error!',
        description: error
      })
    }
  }, [error])

  useEffect(() => {
    isNeedToFetchList && fetchAwsSettingsList()
  }, [fetchAwsSettingsList, isNeedToFetchList])

  const doConfirm = async (awsSettingId: number, e?: React.MouseEvent<HTMLElement>) => {
    e && e.stopPropagation()
    const isRemoved = await removeAwsSetting(awsSettingId)
    if (isRemoved) {
      notification.success({
        message: 'Successfully',
        description: 'Aws setting was deleted successfully!'
      })
    }
  }

  const columns: ColumnsType<AwsSetting> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Access Key ID',
      render: (record: AwsSetting) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.accessKeyId}</div>,
      key: 'accessKeyId'
    },
    {
      title: 'Secret Key',
      render: (record: AwsSetting) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.secretAccessKey}</div>,
      key: 'secretAccessKey'
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region'
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (record: AwsSetting) => (
        <span>
          <Popconfirm
            title='Are you sure delete this setting?'
            onConfirm={(e) => doConfirm(record.id, e)}
            onCancel={(e) => e?.stopPropagation()}
            okText='Yes'
            cancelText='No'
          >
            <a
              href='/#'
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              Remove
            </a>
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <Layout className={styles.root}>
      <Layout.Header style={rootStyles.header}>
        <PageHeader onBack={() => history.goBack()} title='Aws Settings List' extra={[<CreateAwsSetting key='1' />]} />
      </Layout.Header>
      <Layout.Content style={rootStyles.contentBreadcrumbs}>
        <Breadcrumbs
          items={[
            { title: 'Main', url: '/' },
            { title: 'Settings', url: '/' },
            { title: 'Plugins', url: '' },
            { title: 'Aws Settings', url: '/settings/plugins' }
          ]}
        />
      </Layout.Content>
      <Layout.Content className={styles.contentComponent}>
        <Table
          columns={columns}
          dataSource={awsSettings}
          bordered
          rowKey={(record) => record.id}
          loading={isLoading}
          pagination={{
            onChange: changeCurrentPage,
            current: currentPage,
            total
          }}
        />
      </Layout.Content>
    </Layout>
  )
}

export default connector(AwsSettingsList)
